import { useEffect, useState, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { Plus, Database, Trash2, Eye } from 'lucide-react'
import { useDatasetStore } from '@/store/datasetStore'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { useQuery } from '@tanstack/react-query'
import { datasetsApi } from '@/services/api/datasets'
import { LoadingPage } from '@/components/ui/loading-spinner'
import { showErrorToast, showSuccessToast } from '@/utils/error-handler'
import { useTranslation } from 'react-i18next'
import { FilterBar, ActiveFilters, FilterState, FilterConfig, applyFilters } from '@/components/ui/filters'
import type { Dataset } from '@/types/dataset'

function StatusBadge({ status }: { status: string }) {
  const { t } = useTranslation()
  const variants: Record<string, 'default' | 'secondary' | 'destructive'> = {
    ready: 'default',
    processing: 'secondary',
    error: 'destructive',
    deleted: 'secondary',
  }

  const statusLabels: Record<string, string> = {
    ready: t('datasets.status.ready'),
    processing: t('datasets.status.processing'),
    error: t('datasets.status.error') || 'Error',
    deleted: t('datasets.status.deleted') || 'Deleted',
  }

  return (
    <Badge variant={variants[status] || 'secondary'}>
      {statusLabels[status] || status}
    </Badge>
  )
}

export default function DatasetListPage() {
  const { t } = useTranslation()
  const { datasets, loadDatasets, deleteDataset, isLoading } = useDatasetStore()
  const [page, setPage] = useState(1)
  const pageSize = 12 // Fixed page size for now
  const [filterState, setFilterState] = useState<FilterState>({
    search: '',
    selectedOptions: {},
    dateRange: null,
    sortBy: 'createdAt',
    sortOrder: 'desc',
  })

  // Filter configurations
  const filterConfigs: FilterConfig[] = [
    {
      key: 'search',
      label: 'Search',
      type: 'search',
      placeholder: 'Search datasets by name...',
    },
    {
      key: 'status',
      label: 'Status',
      type: 'multiselect',
      options: [
        { value: 'ready', label: 'Ready', count: datasets.filter(d => d.status === 'ready').length },
        { value: 'processing', label: 'Processing', count: datasets.filter(d => d.status === 'processing').length },
        { value: 'error', label: 'Error', count: datasets.filter(d => d.status === 'error').length },
      ],
    },
    {
      key: 'dateRange',
      label: 'Date Range',
      type: 'daterange',
    },
    {
      key: 'sort',
      label: 'Sort',
      type: 'sort',
      options: [
        { value: 'name', label: 'Name' },
        { value: 'createdAt', label: 'Date Created' },
        { value: 'updatedAt', label: 'Date Updated' },
        { value: 'rowCount', label: 'Row Count' },
      ],
    },
  ]

  // Filter functions
  const filterFunctions = {
    status: (dataset: Dataset, value: string) => dataset.status === value,
    dateRange: (dataset: Dataset, dateRange: any) => {
      if (!dateRange || (!dateRange.from && !dateRange.to)) return true;
      const datasetDate = new Date(dataset.createdAt);
      if (dateRange.from && datasetDate < dateRange.from) return false;
      if (dateRange.to && datasetDate > dateRange.to) return false;
      return true;
    },
    sort: (a: Dataset, b: Dataset, sortBy: string) => {
      switch (sortBy) {
        case 'name':
          return a.name.localeCompare(b.name)
        case 'createdAt':
          return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
        case 'updatedAt':
          return new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime()
        case 'rowCount':
          return (a.rowCount || 0) - (b.rowCount || 0)
        default:
          return 0
      }
    },
  }

  // Apply filters to datasets
  const filteredDatasets = useMemo(() => {
    return applyFilters(datasets, filterState, filterFunctions)
  }, [datasets, filterState])

  // Paginate filtered results
  const paginatedDatasets = useMemo(() => {
    const startIndex = (page - 1) * pageSize
    const endIndex = startIndex + pageSize
    return filteredDatasets.slice(startIndex, endIndex)
  }, [filteredDatasets, page, pageSize])

  const totalPages = Math.ceil(filteredDatasets.length / pageSize)

  const { data, refetch, isLoading: queryLoading, error } = useQuery({
    queryKey: ['datasets', page, pageSize, filterState],
    queryFn: async () => {
      // Build API options from current filters
      const apiOptions: any = {
        limit: pageSize,
        offset: (page - 1) * pageSize,
      }

      // Add search filter
      if (filterState.search) {
        apiOptions.search = filterState.search
      }

      // Add selected options
      Object.entries(filterState.selectedOptions).forEach(([key, values]) => {
        if (values.length > 0) {
          if (key === 'status') {
            // For status, use the first selected value
            apiOptions.status = values[0]
          }
        }
      })

      // Add date range - format as YYYY-MM-DD for backend
      if (filterState.dateRange) {
        const formatDate = (date: Date) => {
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          return `${year}-${month}-${day}`;
        };

        const startDate = filterState.dateRange.from ? formatDate(filterState.dateRange.from) : undefined;
        const endDate = filterState.dateRange.to ? formatDate(filterState.dateRange.to) : undefined;

        if (startDate) apiOptions.dateFrom = startDate;
        if (endDate) apiOptions.dateTo = endDate;
      }

      // Add sorting
      if (filterState.sortBy) {
        apiOptions.sortBy = filterState.sortBy
        apiOptions.sortOrder = filterState.sortOrder
      }

      return await datasetsApi.list(apiOptions)
    },
  })

  useEffect(() => {
    if (error) {
      showErrorToast(error as Error, t('errors.loadDatasets'))
    }
  }, [error, t])

  useEffect(() => {
    if (data?.datasets) {
      // Update store with fetched data
      loadDatasets()
    }
  }, [data, loadDatasets])

  // Note: We now use filtered datasets instead of raw query data

  const handleDelete = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation()
    if (confirm(t('common.confirmDelete'))) {
      try {
        await deleteDataset(id)
        refetch()
        showSuccessToast(t('datasets.deleteSuccess'))
      } catch (error) {
        showErrorToast(error, t('errors.deleteDataset'))
      }
    }
  }

  if (queryLoading || isLoading) {
    return <LoadingPage message={t('common.loading')} />
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold">{t('datasets.title')}</h1>
          <p className="text-muted-foreground mt-1">
            {t('datasets.subtitle')}
          </p>
        </div>
        <Link to="/datasets/upload">
          <Button>
            <Plus className="w-4 h-4 mr-2" />
            {t('datasets.uploadDataset')}
          </Button>
        </Link>
      </div>

      {/* Filters */}
      {datasets.length > 0 && (
        <div className="space-y-4">
          <FilterBar
            filters={filterConfigs}
            onChange={setFilterState}
            initialState={filterState}
          />

          <ActiveFilters
            filters={filterConfigs}
            filterState={filterState}
            onRemove={(filterKey, value) => {
              if (filterKey === 'search') {
                setFilterState(prev => ({ ...prev, search: '' }))
              } else if (filterKey === 'daterange') {
                setFilterState(prev => ({ ...prev, dateRange: null }))
              } else if (value) {
                setFilterState(prev => ({
                  ...prev,
                  selectedOptions: {
                    ...prev.selectedOptions,
                    [filterKey]: prev.selectedOptions[filterKey]?.filter(v => v !== value) || []
                  }
                }))
              }
            }}
          />

          <div className="text-sm text-muted-foreground">
            Showing {paginatedDatasets.length} of {filteredDatasets.length} datasets
            {filteredDatasets.length !== datasets.length && (
              <span> (filtered from {datasets.length} total)</span>
            )}
          </div>
        </div>
      )}

      {filteredDatasets.length === 0 && datasets.length > 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Database className="w-12 h-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No datasets match your filters</h3>
            <p className="text-muted-foreground mb-4">
              Try adjusting your search or filter criteria
            </p>
            <Button
              variant="outline"
              onClick={() => setFilterState({
                search: '',
                selectedOptions: {},
                dateRange: null,
                sortBy: 'createdAt',
                sortOrder: 'desc',
              })}
            >
              Clear Filters
            </Button>
          </CardContent>
        </Card>
      ) : filteredDatasets.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Database className="w-12 h-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">{t('datasets.noDatasets')}</h3>
            <p className="text-muted-foreground mb-4">
              {t('datasets.noDatasetsDescription')}
            </p>
            <Link to="/datasets/upload">
              <Button>
                <Plus className="w-4 h-4 mr-2" />
                {t('datasets.uploadDataset')}
              </Button>
            </Link>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {paginatedDatasets.map((dataset) => (
              <Card key={dataset.id} className="hover:shadow-md transition-shadow h-full">
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <CardTitle className="line-clamp-1">{dataset.name}</CardTitle>
                      <div className="mt-1 flex items-center gap-2 text-sm text-muted-foreground">
                        <StatusBadge status={dataset.status} />
                        {dataset.rowCount != null ? (
                          <span>{dataset.rowCount.toLocaleString()} {t('datasets.rows')}</span>
                        ) : (
                          <span className="text-muted-foreground">{t('datasets.processing')}</span>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <Link to={`/datasets/${dataset.id}`}>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <Eye className="w-4 h-4" />
                        </Button>
                      </Link>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={(e) => handleDelete(dataset.id, e)}
                        className="h-8 w-8"
                      >
                        <Trash2 className="w-4 h-4 text-destructive" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 text-sm">
                    {(dataset.symbol || dataset.metadata?.symbol) && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">{t('datasets.symbol')}:</span>
                        <span>{dataset.symbol || dataset.metadata?.symbol}</span>
                      </div>
                    )}
                    {(dataset.timeframe || dataset.metadata?.timeframe) && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">{t('datasets.timeframe')}:</span>
                        <span>{dataset.timeframe || dataset.metadata?.timeframe}</span>
                      </div>
                    )}
                    {dataset.columnCount != null && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">{t('datasets.columns')}:</span>
                        <span>{dataset.columnCount}</span>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-4">
              <button
                onClick={() => setPage(Math.max(1, page - 1))}
                disabled={page <= 1}
                className="px-3 py-1 text-sm border rounded disabled:opacity-50"
              >
                Previous
              </button>
              <span className="text-sm text-muted-foreground">
                Page {page} of {totalPages}
              </span>
              <button
                onClick={() => setPage(Math.min(totalPages, page + 1))}
                disabled={page >= totalPages}
                className="px-3 py-1 text-sm border rounded disabled:opacity-50"
              >
                Next
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
